import { Form, Head, Link } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import VariantRows, {
  type VariantDraft,
} from "@/components/brand/settings/products/variant-rows"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { toMessages } from "@/lib/form-errors"
import {
  brandSettingsProductArchivePath,
  brandSettingsProductPath,
  brandSettingsProductRestorePath,
  brandSettingsProductsPath,
} from "@/routes"
import type { BrandSettingsProductFormProps } from "@/types/products"

/**
 * OKS-932 — Create / edit a manual product.
 *
 * ## Shell: the DEFAULT (narrow) `BrandSettingsLayout` column
 *
 * The Produits INDEX uses `wide` because it is a data table; this screen is a
 * form, which is exactly what the narrow `max-w-xl` column is for
 * (`brand-settings-layout.tsx` doc comment). The two are not in tension —
 * they are the two halves of that same doc comment, and OKS-931's `wide` was
 * the exception it names.
 *
 * ## Form rules are hard constraints
 *
 * Inertia `<Form>` with plain `Input` / `Label` / `Checkbox` + `name`
 * attributes, errors from the render-prop `errors` object through the house
 * `<InputError>`. **No shadcn `Form`/`FormField`/`FormItem`/`FormMessage`, no
 * react-hook-form, no zod** — Rails validations flow back through Inertia.
 * Routes come from js-routes helpers, never a string URL.
 *
 * ## Its own screen rather than a dialog (PRD §7.3)
 *
 * The form carries a file upload plus a repeatable variant list; at 375px
 * that is the worst possible modal. Own routes also give back-button and
 * validation-redirect behaviour for free.
 *
 * ## Price is offered ONLY at zero variant rows (D6)
 *
 * With variants the range derives from them, so asking for a product price
 * the page would then ignore is a field that lies. Adding the first row hides
 * the input and explains why.
 */
export default function BrandSettingsProductForm({
  product,
  currency,
  variant_limit,
}: BrandSettingsProductFormProps) {
  const { t } = useTranslation()
  const isEdit = product.id !== null

  const [variants, setVariants] = useState<VariantDraft[]>(() =>
    product.variants.map((variant) => ({
      id: variant.id,
      title: variant.title,
      sku: variant.sku ?? "",
      price:
        variant.price_cents === null
          ? ""
          : (variant.price_cents / 100).toFixed(2),
      available: variant.available,
    })),
  )

  // Preview precedence (UX §2.2): a newly picked file wins, then the stored
  // image, then the neutral placeholder. `removed` is staged and applied on
  // save — there is no separate DELETE route and no confirm dialog, unlike
  // the brand cover; a product image is low-stakes and undoable by cancelling.
  const [pickedUrl, setPickedUrl] = useState<string | null>(null)
  const [removed, setRemoved] = useState(false)
  const previewUrl = pickedUrl ?? (removed ? null : product.image_url)

  return (
    <AppLayout>
      <Head
        title={t(
          isEdit
            ? "settings.brand.products.form.edit_title"
            : "settings.brand.products.form.create_title",
        )}
      />
      {/* Default (narrow) column — this is a form. See the header. */}
      <BrandSettingsLayout>
        <div data-testid="brand-product-form-page" className="space-y-6">
          <HeadingSmall
            title={t(
              isEdit
                ? "settings.brand.products.form.edit_title"
                : "settings.brand.products.form.create_title",
            )}
            description={t(
              isEdit
                ? "settings.brand.products.form.edit_subtitle"
                : "settings.brand.products.form.create_subtitle",
            )}
          />

          <Form
            action={
              isEdit
                ? brandSettingsProductPath(product.id!)
                : brandSettingsProductsPath()
            }
            method={isEdit ? "patch" : "post"}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="brand-product-form"
          >
            {({ errors, processing, progress }) => (
              <>
                <FormFocus errors={errors} isEdit={isEdit} />
                <div className="grid gap-2">
                  <Label htmlFor="product-image">
                    {t("settings.brand.products.form.image_label")}
                  </Label>

                  {previewUrl === null ? (
                    <div
                      className="bg-muted h-24 w-24 rounded"
                      role="img"
                      aria-label={t("settings.brand.products.image_missing")}
                      data-testid="brand-product-image-placeholder"
                    />
                  ) : (
                    /*
                      Capped so a portrait photo cannot push the title field
                      below the fold on mobile (UX §5).
                    */
                    <img
                      src={previewUrl}
                      alt={t("settings.brand.products.form.image_preview_alt")}
                      className="max-h-48 w-full object-contain"
                      data-testid="brand-product-image-preview"
                    />
                  )}

                  <Input
                    id="product-image"
                    type="file"
                    name="product[image]"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      // Object URL so the preview swaps immediately, never
                      // after the round-trip (UX §4).
                      setPickedUrl(file ? URL.createObjectURL(file) : null)
                      if (file) setRemoved(false)
                    }}
                  />
                  <p className="text-muted-foreground text-xs">
                    {t("settings.brand.products.form.image_helper")}
                  </p>

                  {/* Staged: the hidden field is what the writer reads. */}
                  <input
                    type="hidden"
                    name="product[remove_image]"
                    value={removed ? "1" : "0"}
                  />
                  {previewUrl !== null ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 w-full sm:h-9 sm:w-auto"
                      data-testid="brand-product-image-remove"
                      onClick={() => {
                        setPickedUrl(null)
                        setRemoved(true)
                      }}
                    >
                      {t("settings.brand.products.form.image_remove")}
                    </Button>
                  ) : null}

                  <InputError messages={toMessages(errors.image)} />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="product-title">
                    {t("settings.brand.products.form.title_label")}
                    {/*
                      The visual required marker. `aria-hidden` because the
                      state is conveyed to assistive tech by `aria-required`
                      on the control — a screen reader announcing "star" is
                      noise, and the `*` is not in the locale string so it
                      cannot be dropped by a translator.
                    */}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  {/*
                    ⚠️ NO native `required` — OKS-661/OKS-692 posture, enforced
                    by `bin/check-native-required`.

                    A bare native `required` makes Chrome short-circuit submit
                    and show its OWN untranslated validation bubble. On a
                    FR-default brand that is an English browser tooltip in
                    place of the localized inline error, and the server-side
                    validation this form wires through Inertia never runs at
                    all. `aria-required` keeps the state announced; the server
                    stays the guard.
                  */}
                  <Input
                    id="product-title"
                    name="product[title]"
                    defaultValue={product.title}
                    maxLength={255}
                    aria-required="true"
                  />
                  <InputError messages={toMessages(errors.title)} />
                </div>

                {/*
                  D6 — one source of truth. The product price input exists
                  only while there are no variant rows; once there are, the
                  range derives from them and this field would be a value the
                  page collects and ignores.
                */}
                {variants.length === 0 ? (
                  <div className="grid gap-2">
                    <Label htmlFor="product-price">
                      {t("settings.brand.products.form.price_label")} (
                      {currency})
                    </Label>
                    <Input
                      id="product-price"
                      name="product[price]"
                      inputMode="decimal"
                      defaultValue={
                        product.price_cents === null
                          ? ""
                          : (product.price_cents / 100).toFixed(2)
                      }
                    />
                    <InputError messages={toMessages(errors.price)} />
                  </div>
                ) : (
                  <p
                    className="text-muted-foreground text-sm"
                    data-testid="brand-product-price-from-variants"
                  >
                    {t("settings.brand.products.form.price_from_variants")}
                  </p>
                )}

                <VariantRows
                  variants={variants}
                  onChange={setVariants}
                  limit={variant_limit}
                  currency={currency}
                  errors={errors}
                />

                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button
                    asChild
                    variant="outline"
                    className="h-11 w-full sm:h-9 sm:w-auto"
                  >
                    <Link href={brandSettingsProductsPath()}>
                      {t("settings.brand.products.form.cancel")}
                    </Link>
                  </Button>
                  <Button
                    type="submit"
                    className="h-11 w-full sm:h-9 sm:w-auto"
                    disabled={processing}
                    data-testid="brand-product-submit"
                  >
                    {/*
                      A 5 MB image on mobile data is otherwise a dead UI for
                      several seconds, so the percentage is shown whenever
                      Inertia reports upload progress (UX §3.2).
                    */}
                    {processing && progress
                      ? t("settings.brand.products.form.submitting_progress", {
                          percent: progress.percentage ?? 0,
                        })
                      : processing
                        ? t("settings.brand.products.form.submitting")
                        : t("settings.brand.products.form.submit")}
                  </Button>
                </div>
              </>
            )}
          </Form>

          {/*
            Archive / restore sits visually AND in the DOM after the save
            controls (UX §6): it is never the first thing a keyboard user
            reaches, and it is a separate form so it cannot submit the edits.
          */}
          {isEdit ? (
            <div className="border-t pt-4">
              {product.archived ? (
                <Form
                  action={brandSettingsProductRestorePath(product.id!)}
                  method="post"
                >
                  {({ processing }) => (
                    <Button
                      type="submit"
                      variant="outline"
                      className="h-11 w-full sm:h-9 sm:w-auto"
                      disabled={processing}
                      data-testid="brand-product-restore"
                    >
                      {t("settings.brand.products.form.restore")}
                    </Button>
                  )}
                </Form>
              ) : (
                <ArchiveDialog productId={product.id!} />
              )}
            </div>
          ) : null}
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}

/**
 * OKS-932 — the form's ONLY focus mechanism (UX §3.2: "the first invalid
 * control receives focus on render").
 *
 * ## Why this replaced a bare `autoFocus` on the title
 *
 * `autoFocus` sat unconditionally on the title `<Input>`, which is wrong in
 * two directions and right in one:
 *
 *   - After a **price** or **image** validation failure it focused **title** —
 *     a valid field. The brand lands on a control that is not the problem and
 *     has to hunt for the one that is, which is the opposite of what §3.2 asks
 *     for.
 *   - On the **edit** screen it stole focus on load when nothing had failed,
 *     popping the mobile keyboard and skipping past the heading. Worse than
 *     the create case precisely because nothing was wrong.
 *   - On **create with no errors** it was correct, and that behaviour is kept.
 *
 * ## One mechanism, not two
 *
 * `autoFocus` fires during mount and an effect runs after it, so keeping both
 * would mean two things racing for the same control with the effect always
 * winning — a `autoFocus` that reads as load-bearing and is not. It is gone
 * from the JSX entirely; every focus decision is here.
 *
 * ⚠️ Order is DOM order, not object order. `errors` is a plain object and its
 * key order is whatever the server serialized; focusing "the first key" would
 * focus whichever field Rails happened to list first. The brand should land on
 * the topmost failing control, so the order is declared explicitly below and
 * must be kept in sync with the field order in the form.
 *
 * `variants` maps to the first variant row's name input rather than to the
 * fieldset: the fieldset is not focusable, and row 0 is the closest focusable
 * thing to where the message renders.
 */
function FormFocus({
  errors,
  isEdit,
}: {
  errors: Record<string, string | string[] | undefined>
  isEdit: boolean
}) {
  useEffect(() => {
    // Declared DOM order — see the header.
    const order: [string, string][] = [
      ["image", "product-image"],
      ["title", "product-title"],
      ["price", "product-price"],
      ["variants", "variant-title-0"],
    ]

    const firstInvalid = order.find(
      ([key, id]) => toMessages(errors[key]) && document.getElementById(id),
    )

    if (firstInvalid) {
      document.getElementById(firstInvalid[1])?.focus()
      return
    }

    // No errors: focus the title on CREATE only. On edit the brand arrived to
    // read an existing product, so taking focus is an interruption.
    if (!isEdit) document.getElementById("product-title")?.focus()
  }, [errors, isEdit])

  return null
}

/**
 * Archiving is confirmed; restoring is not — it is not destructive (UX §2.3).
 *
 * ⚠️ Base UI: `AlertDialogTrigger` takes a `render` prop, NOT `asChild`.
 * First-party Radix is removed and a zero-radix guard reds any Radix import. The
 * in-repo precedent is `shopify-integration-card.tsx`.
 */
function ArchiveDialog({ productId }: { productId: number }) {
  const { t } = useTranslation()

  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <Button
            type="button"
            variant="outline"
            className="text-destructive h-11 w-full sm:h-9 sm:w-auto"
            data-testid="brand-product-archive-trigger"
          >
            {t("settings.brand.products.form.archive")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="brand-product-archive-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("settings.brand.products.archive_dialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("settings.brand.products.archive_dialog.body")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form action={brandSettingsProductArchivePath(productId)} method="post">
          {({ processing }) => (
            <AlertDialogFooter>
              {/* Cancel holds primary focus — the `RevokeDialog` precedent. */}
              <AlertDialogCancel>
                {t("settings.brand.products.archive_dialog.cancel")}
              </AlertDialogCancel>
              <Button
                type="submit"
                variant="destructive"
                disabled={processing}
                data-testid="brand-product-archive-confirm"
              >
                {t("settings.brand.products.archive_dialog.confirm")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
